import React from "react";
import injectSheet from "react-jss";
import { DropTarget } from "react-dnd";

// import TourCanvasDragLayer from "./TourCanvasDragLayer";
import TourCanvasBackground from "./TourCanvasBackground";
import TourCanvasGrid from "./TourCanvasGrid";
import TourCanvasImage from "./TourCanvasImage";
import { matchesStudioFilter } from "./studio/imageFilter";
import { marqueeSelect } from "./studio/marqueeSelect";
import { limitWidth } from "../../lib/player/src/TextureManager.js";

export const gridUnit = 20;
const maxDrag = 200;

// The pasteboard is finite: nodes can only be placed within this many grid
// units of the origin in either direction. This matches the pan clamp so the
// whole board is always reachable.
export const BOARD_LIMIT = maxDrag;

// Footprint of a node in grid units (img is 200x182px, gridUnit is 20px).
// Used for snap spacing and overlap avoidance when placing nodes.
const NODE_PIXEL_W = 200;
const NODE_PIXEL_H = 182;
const NODE_GRID_W = NODE_PIXEL_W / gridUnit;
const NODE_GRID_H = Math.ceil(NODE_PIXEL_H / gridUnit);
// Rendered size in grid units — nodes are anchored at their top-left corner
// (image.x, image.y) with +y up, so the thumbnail extends right and down.
const NODE_VISUAL_W = NODE_PIXEL_W / gridUnit;
const NODE_VISUAL_H = NODE_PIXEL_H / gridUnit;

// Soft capacity: how many node footprints fit on the board. Used for the
// "X / Y nodes" readout so users can see how full the board is.
export const BOARD_CAPACITY = Math.floor(
  ((2 * BOARD_LIMIT) / NODE_GRID_W) * ((2 * BOARD_LIMIT) / NODE_GRID_H),
);

const clampToBoard = value => {
  if (value > BOARD_LIMIT) return BOARD_LIMIT;
  if (value < -BOARD_LIMIT) return -BOARD_LIMIT;
  return value;
};

// Manual wheel zoom is capped for comfort; programmatic fit-to-view may go wider
// so every node on the board can actually be framed.
const MANUAL_ZOOM_MIN = 0.6;
const MANUAL_ZOOM_MAX = 1.5;
const FIT_ZOOM_MIN = 0.05;
const FIT_ZOOM_MAX = 4;

/**
 * TourCanvas — the pasteboard/camera.
 *
 * Canvas API (exposed once on mount via `props.onReady(api)`; held by
 * NodeEditor and consumed by the studio panels — they never reach into canvas
 * internals):
 *
 *   api.fitToView({ all: true })     // frame every node (toolbar "Fit All")
 *   api.fitToView()                  // frame filtered/visible nodes (eased)
 *   api.fitToImages(ids)             // frame a subset of node ids (eased)
 *   api.jumpTo(worldX, worldY)       // centre a world point (eased)
 *   api.panBy(dx, dy)                // nudge pan, clamped via clampToBoard (eased)
 *   api.zoomBy(delta)                // wraps onChangeZoom (0.6–1.5)
 *   api.animateTo({x, y, scale}, ms) // eased camera move (easeOutCubic)
 *   api.getCameraState()             // { x, y, scale, canvasRect }
 *
 * Props of note:
 *   - onReady(api)  — called once after mount with the API above.
 *   - filter        — { status: [], floor: null|string, tags: [] }; applied
 *                     when rendering nodes (the UI that sets it is WP-E).
 *   - tool          — 'pan' | 'select' (WP-L). In 'pan' a left-drag on empty
 *                     board pans; in 'select' a left-drag draws a marquee that
 *                     multi-selects the nodes inside it.
 *   - selectedIds   — string[] of marquee-selected node ids (rendered with a
 *                     distinct highlight). Owned by NodeEditor.
 *   - onSelectImages(ids) — called when a marquee finishes (or an empty-canvas
 *                     click clears the selection).
 *
 * Motion: programmatic moves (fit/jump/pan) ease through `animateTo`; direct
 * mouse-drag panning stays instant. An in-flight camera animation is cancelled
 * when the user grabs the board or the component unmounts.
 */
class TourCanvas extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      canvasRect: this.getCanvasRect(),
      scale: 1,
      dragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      x: 0,
      y: 0,
      curX: 0,
      curY: 0,
      isNodeDrawing: false,
      // Marquee multi-select (WP-L). Null when not dragging a selection box;
      // otherwise { startX, startY, curX, curY } in client coords plus `moved`
      // (true once the drag passes the click threshold).
      marquee: null,
      showHotSpot: false,
      currentHotSpot: null,
      startX: null,
      startY: null,
      canvasOffset: null,
      targetImageInfo: {
        id: null,
        ratio: null,
      },
      canvasImageSize: {
        width: 0,
        height: 0,
      },
    };

    this.updateCanvasRect = this.updateCanvasRect.bind(this);
    this.handleCanvasResize = this.handleCanvasResize.bind(this);

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.finishMarquee = this.finishMarquee.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.handleNodeDrawing = this.handleNodeDrawing.bind(this);
    this.handleAddHotSpot = this.handleAddHotSpot.bind(this);
    this.handleSetTargetImageInfo = this.handleSetTargetImageInfo.bind(this);

    this.updateCanvasNode = this.updateCanvasNode.bind(this);
    this.drawNodeLink = this.drawNodeLink.bind(this);
    this.clearNodeLink = this.clearNodeLink.bind(this);
    this.setCanvasImageSize = this.setCanvasImageSize.bind(this);

    this.fitToView = this.fitToView.bind(this);
    this.fitToImages = this.fitToImages.bind(this);
    this.jumpTo = this.jumpTo.bind(this);
    this.panBy = this.panBy.bind(this);
    this.zoomBy = this.zoomBy.bind(this);
    this.animateTo = this.animateTo.bind(this);
    this.getCameraState = this.getCameraState.bind(this);
    this.getCanvasApi = this.getCanvasApi.bind(this);
    this.getVisibleImages = this.getVisibleImages.bind(this);
    this.placeImage = this.placeImage.bind(this);

    // Handle for the in-flight camera animation (animateTo).
    this._camRAF = null;
  }

  componentDidMount() {
    addMicrosoftEdgeListeners();

    this.updateCanvasRect();

    window.addEventListener("resize", this.handleCanvasResize);
    if (typeof ResizeObserver !== "undefined" && this.canvas) {
      this._resizeObserver = new ResizeObserver(this.handleCanvasResize);
      this._resizeObserver.observe(this.canvas);
    }

    // Add mouse listeners
    this.canvas.addEventListener("mousedown", this.onMouseDown);
    this.canvas.addEventListener("mousemove", this.onMouseMove);
    this.canvas.addEventListener("mouseup", this.onMouseUp);

    this.canvas.addEventListener("wheel", this.onWheel);

    this.updateCanvasNode();

    const currentInfo = this.props.currentInfo;

    if (currentInfo) {
      this.setState({
        x: currentInfo.x,
        y: currentInfo.y,
        scale: currentInfo.scale,
      });
    }

    if (this.props.onReady) {
      this.props.onReady(this.getCanvasApi());
    }
  }

  componentDidUpdate(prevProps) {
    // Auto-fit whenever the studio filter (floor/area/status/tags) changes, so
    // selecting a floor/area reframes the board onto just those nodes.
    if (
      JSON.stringify(prevProps.filter) !== JSON.stringify(this.props.filter)
    ) {
      requestAnimationFrame(this.fitToView);
    }
  }

  componentWillUnmount() {
    cancelAnimationFrame(this._camRAF);

    var currentInfo = {
      x: this.state.x,
      y: this.state.y,
      scale: this.state.scale,
    };

    this.props.onSaveCurrentInfo(currentInfo);

    window.removeEventListener("resize", this.handleCanvasResize);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  onUpdateImage(image) {
    this.props.onUpdateImage(image);
  }

  onMouseDown(e) {
    if (!this.state.showHotSpot) {
      this.updateCanvasNode();
      this.setState({
        startX: e.pageX - this.state.canvasOffset.x,
        startY: e.pageY - this.state.canvasOffset.y,
      });
    }

    if (e.target !== this.canvas) {
      return;
    }

    this.clearNodeLink();

    this.setState({ showHotSpot: false });

    if (e.buttons === 1) {
      // Grabbing the board interrupts any eased camera move so the drag is 1:1.
      cancelAnimationFrame(this._camRAF);

      if (this.props.tool === "select") {
        // Select tool: a left-drag on empty board draws a marquee instead of
        // panning. `moved` stays false until the drag passes the threshold so a
        // plain click can clear the selection on mouse-up.
        this.setState({
          marquee: {
            startX: e.clientX,
            startY: e.clientY,
            curX: e.clientX,
            curY: e.clientY,
            moved: false,
          },
        });
      } else {
        this.setState({
          dragging: true,
          lastMouseX: e.clientX,
          lastMouseY: e.clientY,
        });
      }
    }
  }

  onMouseUp(e) {
    // Always resolve an in-flight marquee, even if the pointer ended off-canvas.
    if (this.state.marquee) {
      this.finishMarquee();
    }

    this.setState({ dragging: false });

    if (e.target !== this.canvas) {
      return;
    }
    if (this.state.isNodeDrawing) {
      this.clearNodeLink();
    }
    this.setState({ isNodeDrawing: false });
  }

  onMouseMove(e) {
    if (this.state.marquee) {
      // Once movement exceeds a few pixels it's a real marquee drag, not a click.
      const moved =
        this.state.marquee.moved ||
        Math.abs(e.clientX - this.state.marquee.startX) > 3 ||
        Math.abs(e.clientY - this.state.marquee.startY) > 3;
      this.setState({
        marquee: {
          ...this.state.marquee,
          curX: e.clientX,
          curY: e.clientY,
          moved,
        },
      });
      return;
    }

    if (this.state.isNodeDrawing) {
      this.setState({ curX: e.pageX, curY: e.pageY });
      this.drawNodeLink(e);
      return;
    }

    if (this.state.dragging === false) {
      return;
    }

    // Calculate delta in grid units
    const deltaX =
      (e.clientX - this.state.lastMouseX) / (gridUnit * this.state.scale);
    const deltaY =
      (e.clientY - this.state.lastMouseY) / (gridUnit * this.state.scale);

    let x = this.state.x + deltaX;
    let y = this.state.y - deltaY;

    if (x > maxDrag) {
      x = maxDrag;
    } else if (x < -maxDrag) {
      x = -maxDrag;
    }

    if (y > maxDrag) {
      y = maxDrag;
    } else if (y < -maxDrag) {
      y = -maxDrag;
    }

    this.setState({
      x,
      y,
      lastMouseX: e.clientX,
      lastMouseY: e.clientY,
    });
  }

  onWheel(e) {
    if (e.deltaY > 0) {
      this.onChangeZoom(-0.1);
    } else {
      this.onChangeZoom(0.1);
    }

    this.setState({ showHotSpot: false });
    this.clearNodeLink();
  }

  // Resolve a marquee drag: convert its two screen corners to world coordinates,
  // hit-test every node through the pure `marqueeSelect` helper, and report the
  // result up. A non-drag (plain click on empty board) clears the selection.
  finishMarquee() {
    const m = this.state.marquee;
    this.setState({ marquee: null });

    if (!m) {
      return;
    }

    if (!m.moved) {
      if (this.props.onSelectImages) this.props.onSelectImages([]);
      return;
    }

    const a = this.getXYFromMouse(m.startX, m.startY);
    const b = this.getXYFromMouse(m.curX, m.curY);
    const ids = marqueeSelect(this.props.images, {
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y,
    });

    if (this.props.onSelectImages) this.props.onSelectImages(ids);
  }

  onChangeZoom(delta) {
    let { scale } = this.state;

    scale += delta;

    // Limit scale (manual zoom only — fit-to-view uses FIT_ZOOM_* instead)
    if (scale < MANUAL_ZOOM_MIN) {
      scale = MANUAL_ZOOM_MIN;
    } else if (scale > MANUAL_ZOOM_MAX) {
      scale = MANUAL_ZOOM_MAX;
    }

    this.setState({ scale });
  }

  // Compute the camera target {x, y, scale} that frames the given node list,
  // or null if it can't be computed (no nodes / canvas not measured yet).
  computeFitTarget(imageList) {
    const { width, height } = this.state.canvasRect;

    if (!imageList || imageList.length === 0 || !width || !height) {
      return null;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const image of imageList) {
      minX = Math.min(minX, image.x);
      maxX = Math.max(maxX, image.x + NODE_VISUAL_W);
      minY = Math.min(minY, image.y - NODE_VISUAL_H);
      maxY = Math.max(maxY, image.y);
    }

    // Pad by a node footprint so edge nodes aren't flush against the border.
    const spanX = maxX - minX + NODE_VISUAL_W * 2;
    const spanY = maxY - minY + NODE_VISUAL_H * 2;

    const scaleX = width / (spanX * gridUnit);
    const scaleY = height / (spanY * gridUnit);
    let scale = Math.min(scaleX, scaleY);

    if (scale < FIT_ZOOM_MIN) scale = FIT_ZOOM_MIN;
    else if (scale > FIT_ZOOM_MAX) scale = FIT_ZOOM_MAX;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return {
      scale,
      x: clampToBoard(-centerX),
      y: clampToBoard(-centerY),
    };
  }

  // Centre and zoom the board so every node in the target set is in view.
  // By default respects the studio filter (floor/area/status). Pass
  // `{ all: true }` to frame every node on the board (toolbar "Fit All").
  fitToView({ all = false } = {}) {
    const allImages = Object.values(this.props.images);
    const visible = all ? [] : this.getVisibleImages();
    const imageList =
      all || visible.length === 0 ? allImages : visible;

    const target = this.computeFitTarget(imageList);

    if (!target) {
      // Canvas rect is often 0×0 on the first frame after mount — retry once
      // measured instead of snapping back to the default camera.
      if (imageList.length > 0) {
        requestAnimationFrame(() => this.fitToView({ all }));
      }
      return;
    }

    this.animateTo(target);
  }

  // Generalised fitToView: frame only the given subset of node ids.
  fitToImages(ids) {
    if (!ids || ids.length === 0) {
      return;
    }

    const wanted = new Set(ids.filter(id => id != null));
    if (wanted.size === 0) {
      return;
    }

    const subset = Object.values(this.props.images).filter(img =>
      wanted.has(img.id),
    );
    const target = this.computeFitTarget(subset);

    if (!target) {
      if (subset.length > 0) {
        requestAnimationFrame(() => this.fitToImages(ids));
      }
      return;
    }

    this.animateTo(target);
  }

  // Pan so the given world (grid) coordinate sits at the centre of the canvas.
  jumpTo(worldX, worldY) {
    this.animateTo({
      x: clampToBoard(-worldX),
      y: clampToBoard(-worldY),
    });
  }

  // Nudge the pan by a delta in grid units, clamped to the board.
  panBy(dx, dy) {
    this.animateTo(
      {
        x: clampToBoard(this.state.x + dx),
        y: clampToBoard(this.state.y + dy),
      },
      200,
    );
  }

  // Wraps onChangeZoom so panels can zoom through the API.
  zoomBy(delta) {
    this.onChangeZoom(delta);
  }

  // Snapshot of the camera, for overlays/indicators that need to project nodes.
  getCameraState() {
    return {
      x: this.state.x,
      y: this.state.y,
      scale: this.state.scale,
      canvasRect: this.state.canvasRect,
    };
  }

  // The public Canvas API handed to NodeEditor via props.onReady on mount.
  getCanvasApi() {
    return {
      fitToView: this.fitToView,
      fitToImages: this.fitToImages,
      jumpTo: this.jumpTo,
      panBy: this.panBy,
      zoomBy: this.zoomBy,
      animateTo: this.animateTo,
      getCameraState: this.getCameraState,
    };
  }

  // Eased camera move toward a target (any of x/y/scale). easeOutCubic.
  // Pass duration <= 0 for an instant set. Cancels any in-flight animation.
  animateTo({ x, y, scale } = {}, duration = 350) {
    const start = {
      x: this.state.x,
      y: this.state.y,
      scale: this.state.scale,
    };
    const target = {
      x: clampToBoard(x ?? start.x),
      y: clampToBoard(y ?? start.y),
      scale: scale ?? start.scale,
    };

    cancelAnimationFrame(this._camRAF);

    if (duration <= 0) {
      this.setState(target);
      return;
    }

    const t0 = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = now => {
      const t = Math.min(1, (now - t0) / duration);
      const k = ease(t);
      this.setState({
        x: start.x + (target.x - start.x) * k,
        y: start.y + (target.y - start.y) * k,
        scale: start.scale + (target.scale - start.scale) * k,
      });
      if (t < 1) {
        this._camRAF = requestAnimationFrame(step);
      }
    };

    this._camRAF = requestAnimationFrame(step);
  }

  // Find the nearest free grid cell to (x, y) so dropped nodes don't stack on
  // top of each other. Searches in an expanding ring around the target.
  findFreeCell(x, y, excludeId) {
    const others = Object.values(this.props.images).filter(
      img => img.id !== excludeId,
    );

    const overlaps = (cx, cy) =>
      others.some(
        img =>
          Math.abs(img.x - cx) < NODE_GRID_W &&
          Math.abs(img.y - cy) < NODE_GRID_H,
      );

    if (!overlaps(x, y)) {
      return { x, y };
    }

    for (let ring = 1; ring <= 40; ring++) {
      const step = ring;
      for (let dx = -step; dx <= step; dx++) {
        for (let dy = -step; dy <= step; dy++) {
          // Only test the outer ring, not cells we've already checked.
          if (Math.abs(dx) !== step && Math.abs(dy) !== step) continue;

          const cx = clampToBoard(x + dx * NODE_GRID_W);
          const cy = clampToBoard(y + dy * NODE_GRID_H);

          if (!overlaps(cx, cy)) {
            return { x: cx, y: cy };
          }
        }
      }
    }

    return { x, y };
  }

  // Clamp a raw drop position to the board and snap it to a free cell.
  placeImage(rawX, rawY, excludeId) {
    return this.findFreeCell(
      clampToBoard(rawX),
      clampToBoard(rawY),
      excludeId,
    );
  }

  // Apply the studio filter prop { status, floor, tags } to a single node.
  // The actual matching lives in the shared `matchesStudioFilter` helper so the
  // canvas and the minimap (StudioInspector) stay in sync; WP-0 wired the prop
  // through and WP-E completed the status axis + floor → image mapping.
  matchesFilter(image) {
    return matchesStudioFilter(image, this.props.filter, this.props.floorIndex);
  }

  getVisibleImages() {
    return Object.values(this.props.images).filter(image =>
      this.matchesFilter(image),
    );
  }

  handleNodeDrawing(value) {
    this.setState({ isNodeDrawing: value });
  }

  handleAddHotSpot(value) {
    this.setState({ showHotSpot: value });
  }

  handleSetTargetImageInfo(value) {
    this.setState({ targetImageInfo: value });
  }

  getCanvasRect() {
    if (this.canvas) {
      return this.canvas.getBoundingClientRect();
    }

    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  }

  getPosition(image) {
    // Position before offsetting with canvas
    const imageLeft = (image.x + this.state.x) * gridUnit * this.state.scale;

    // Top is minus as the x,y follows coordinate convention
    // In CSS, 0,0 is top left rather than bottom left
    const imageTop = -(image.y + this.state.y) * gridUnit * this.state.scale;

    const { width, height } = this.state.canvasRect;
    const leftOffset = width / 2;
    const topOffset = height / 2;

    return {
      left: imageLeft + leftOffset,
      top: imageTop + topOffset,
    };
  }

  getXYFromMouse(clientX, clientY) {
    // Get distance from center of canvas in pixels
    const { width, height } = this.state.canvasRect;
    const fromX = clientX - 16 - this.state.canvasRect.x - width / 2;
    const fromY = clientY - 16 - this.state.canvasRect.y - height / 2;

    const x = Math.round(fromX / (gridUnit * this.state.scale) - this.state.x);
    const y = Math.round(-fromY / (gridUnit * this.state.scale) - this.state.y);

    return { x, y };
  }

  updateCanvasRect() {
    const next = this.getCanvasRect();
    const prev = this.state.canvasRect;

    // When the canvas element resizes (e.g. inspector collapse), keep the same
    // world point at the same screen position by nudging pan — otherwise nodes
    // and off-screen indicators jump because the viewport centre moved.
    if (
      prev &&
      prev.width &&
      next.width &&
      (prev.width !== next.width || prev.height !== next.height)
    ) {
      const { scale, x, y } = this.state;
      const panScale = gridUnit * scale;
      this.setState({
        canvasRect: next,
        x: clampToBoard(x + (prev.width - next.width) / (2 * panScale)),
        y: clampToBoard(y + (prev.height - next.height) / (2 * panScale)),
      });
      return;
    }

    this.setState({ canvasRect: next });
  }

  handleCanvasResize() {
    this.updateCanvasRect();
    this.updateCanvasNode();
  }

  updateCanvasNode() {
    const offset = this.canvas_node.getBoundingClientRect();
    this.canvas_node.width = offset.width;
    this.canvas_node.height = offset.height;
    this.setState({ canvasOffset: offset });
  }

  drawNodeLink(e) {
    const ctx = this.canvas_node.getContext("2d");
    ctx.clearRect(0, 0, this.canvas_node.width, this.canvas_node.height);
    ctx.beginPath();
    ctx.setLineDash([15, 15]);
    ctx.moveTo(this.state.startX, this.state.startY);
    ctx.lineTo(
      e.pageX - this.state.canvasOffset.x,
      e.pageY - this.state.canvasOffset.y,
    );
    ctx.strokeStyle = "rgba(214, 214, 212, 0.4)";
    ctx.lineWidth = 10 * this.state.scale;
    ctx.lineCap = "round";
    ctx.stroke();
  }

  clearNodeLink() {
    const ctx = this.canvas_node.getContext("2d");
    ctx.clearRect(0, 0, this.canvas_node.width, this.canvas_node.height);
    ctx.beginPath();
  }

  setCanvasImageSize(value) {
    this.setState({
      canvasImageSize: value,
    });
  }

  render() {
    if (this.state.mounted === false) {
      return null;
    }

    const {
      connectDropTarget,
      classes,
      images,
      activeImage,
      onUpdateActiveImage,
      onUpdateImage,
      onDeleteImage,
    } = this.props;

    // Board boundary rectangle, in screen space, following pan/zoom.
    const boundsTopLeft = this.getPosition({ x: -BOARD_LIMIT, y: BOARD_LIMIT });
    const boundsBottomRight = this.getPosition({
      x: BOARD_LIMIT,
      y: -BOARD_LIMIT,
    });
    const boundsStyle = {
      left: boundsTopLeft.left,
      top: boundsTopLeft.top,
      width: boundsBottomRight.left - boundsTopLeft.left,
      height: boundsBottomRight.top - boundsTopLeft.top,
    };

    const visibleImages = this.getVisibleImages();

    const selectedSet = new Set(this.props.selectedIds ?? []);

    // Marquee rectangle in screen space, relative to the canvas root.
    const m = this.state.marquee;
    let marqueeStyle = null;
    if (m && m.moved) {
      const rect = this.state.canvasRect;
      marqueeStyle = {
        left: Math.min(m.startX, m.curX) - rect.x,
        top: Math.min(m.startY, m.curY) - rect.y,
        width: Math.abs(m.curX - m.startX),
        height: Math.abs(m.curY - m.startY),
      };
    }

    // Cursor reflects the active tool: grab/grabbing for pan, crosshair for the
    // marquee select tool.
    let cursor = "grab";
    if (this.props.tool === "select") {
      cursor = "crosshair";
    } else if (this.state.dragging) {
      cursor = "grabbing";
    }

    return connectDropTarget(
      <div className={classes.canvas}>
        <TourCanvasBackground
          background={activeImage !== null && limitWidth(images[activeImage].urls.full)}
        />
        <TourCanvasGrid
          x={this.state.x}
          y={this.state.y}
          scale={this.state.scale}
          gridUnit={gridUnit}
          images={visibleImages}
          canvasRect={this.state.canvasRect}
          canvasImageSize={this.state.canvasImageSize}
          active={activeImage}
        />
        <div className={classes.boardBounds} style={boundsStyle} />
        <canvas
          className={classes.canvasNode}
          ref={ref => {
            this.canvas_node = ref;
          }}
        />
        <div
          className={classes.imagesContainer}
          style={{ cursor }}
          ref={ref => {
            this.canvas = ref;
          }}
        >
          {visibleImages.map(image => {
              return (
                <TourCanvasImage
                  image={image}
                  alts={(image.alts ?? []).map(alt => images[alt])}
                  key={image.id}
                  position={this.getPosition(image)}
                  scale={this.state.scale}
                  active={image.id === activeImage}
                  selected={selectedSet.has(image.id)}
                  onUpdateActiveImage={onUpdateActiveImage}
                  onUpdateImage={onUpdateImage}
                  onDeleteImage={onDeleteImage}
                  onUpdateBoomerang={this.props.onUpdateBoomerang}
                  tour={this.props.tour}
                  activeImage={activeImage}
                  onEmbedTour={this.props.onEmbedTour}
                  handleNodeDrawing={value => this.handleNodeDrawing(value)}
                  isNodeDrawing={this.state.isNodeDrawing}
                  onAddHotSpot={value => this.handleAddHotSpot(value)}
                  showHotSpot={this.state.showHotSpot}
                  handleSetTargetImageInfo={value =>
                    this.handleSetTargetImageInfo(value)
                  }
                  targetImageInfo={this.state.targetImageInfo}
                  clearNodeLink={this.clearNodeLink}
                  setCanvasImageSize={value => this.setCanvasImageSize(value)}
                />
              );
            })}
        </div>
        {marqueeStyle && (
          <div className={classes.marquee} style={marqueeStyle} />
        )}
        {/* <TourCanvasDragLayer
          setCanvasImageSize={value => this.setCanvasImageSize(value)}
          scale={this.state.scale}
        /> */}
        {/* Minimap, zoom and fit controls now live in StudioInspector (WP-B). */}
      </div>,
    );
  }
}

const canvasTarget = {
  drop(props, monitor, component) {
    const itemType = monitor.getItemType();
    const result = monitor.getClientOffset();

    // added a manual offset so that it drops with the mouse over the dragging icon
    // suggestions welcome for a better way to do this
    const raw = component.getXYFromMouse(result.x - 5, result.y - 5);

    if (itemType === "image") {
      const item = monitor.getItem();

      const { image } = item;
      const { x, y } = component.placeImage(raw.x, raw.y, image.id);
      component.onUpdateImage({ ...image, x, y });
    }

    if (itemType === "new-image") {
      // const rect = component.getCanvasRect();
      const { x, y } = component.placeImage(raw.x, raw.y, null);
      return { name: "Canvas", x, y };
    }
  },
};

const styles = theme => ({
  canvas: {
    flex: 1,
    position: "relative",
    backgroundColor: "#333",
    overflow: "hidden",
  },
  imagesContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  boardBounds: {
    position: "absolute",
    border: `2px dashed ${theme.colorGrey}`,
    boxSizing: "border-box",
    pointerEvents: "none",
    zIndex: 0,
  },
  marquee: {
    position: "absolute",
    border: `1px solid ${theme.colorPrimary}`,
    backgroundColor: "rgba(7, 155, 170, 0.12)",
    boxSizing: "border-box",
    pointerEvents: "none",
    zIndex: 5,
  },
  canvasNode: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
});

//----------- MS EDGE POLYFILL -------------//
function addMicrosoftEdgeListeners() {
  // console.log("no edge polyfill");
  // window.addEventListener("drop", (e) => {
  //     // saves mouse position on latest drop
  //     window.stupid_edge_x = e.clientX+"px";
  //     window.stupid_edge_y = e.clientY-40+"px";
  //   }
  // );
  //
  // var targetNode = document.querySelector("div[class*='DropTarget(TourCanvas)-imagesContainer']");
  // // Options for the observer (which mutations to observe)
  // var config = { childList: true };
  //
  // var callback = function(mutationsList) {
  //   for(var mutation of mutationsList) {
  //     if (mutation.type === 'childList') {
  //       var img = mutation.addedNodes[0];
  //       if (img !== undefined && img.style.left === "") {
  //         // assigns saved mouse position if image node wasnt placed properly by react-dnd
  //         img.style.left = window.stupid_edge_x;
  //         img.style.top = window.stupid_edge_y;
  //       }
  //     }
  //   }
  // };
  //
  // if (typeof(targetNode) === "object") {
  //   var observer = new MutationObserver(callback);
  //   observer.observe(targetNode, config);
  // }
}

export default injectSheet(styles)(
  DropTarget(["new-image", "image"], canvasTarget, connect => ({
    connectDropTarget: connect.dropTarget(),
  }))(TourCanvas),
);
